/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { ENTITLEMENT_LIST_SORT_ACTION_MAPPING } from '../../../shared/constants/entitlements';
import { type InsightItemTypes } from '../../../shared/types';
import { type EntitlementDetailsRowType } from '../data-layer';
import type { SodEntitlementListProps } from '../data-layer/entitlementListTypes';
import {
  type EntitlementPagination,
  type EntitlementSidePanelRowContextState,
  type EntitlementSortState,
} from '../data-layer/entitlementTypes';

/**
 * Entitlement List Utility Functions
 *
 * Collection of utility functions for processing and transforming entitlement
 * data for API calls, UI display, and state management. Provides centralized
 * data transformation logic for the entitlement violations feature.
 *
 * Key Functions:
 * - API payload construction for entitlement list queries
 * - Filter data transformation and mapping
 * - Row tracker state configuration for UI interactions
 * - Side panel context data extraction
 * - Unique value extraction for filter options
 * - Sort configuration and mapping
 *
 * Data Transformations:
 * - Converts filter states to API-compatible formats
 * - Maps UI sort options to backend sort parameters
 * - Extracts and formats data for side panel display
 * - Configures tracking metadata for list row interactions
 *
 * @fileoverview Utility functions for entitlement list data processing
 */

type FilterMapValueType = string[] | undefined;

/**
 * Creates filter payload for entitlement list API
 * Transforms filter map to structured API format
 */
const getFilterPayload = (
  filterMap: Record<string, FilterMapValueType>,
  searchKey: string | null
) => {
  const entitlementValueKeys = (filterMap.entitlementValueKeys ?? [])
    .filter((v) => v != null && v !== '' && !Number.isNaN(Number(v)))
    .map((v) => Number(v));
  const entitlementTypeKeys = (filterMap.entitlementTypeKeys ?? [])
    .filter((v) => v != null && v !== '' && !Number.isNaN(Number(v)))
    .map((v) => Number(v));
  const riskIds = (filterMap.riskIds ?? [])
    .filter((v) => v != null && v !== '' && !Number.isNaN(Number(v)))
    .map((v) => Number(v));
  const endpointKeys = (filterMap.endpointKeys ?? [])
    .filter((v) => v != null && v !== '' && !Number.isNaN(Number(v)))
    .map((v) => Number(v));

  return {
    entitlement: {
      entitlementValueKeys,
      entitlementTypeKeys,
      entitlementDisplayName: filterMap.entitlementDisplayName ?? [],
      entitlementTypeName: filterMap.entitlementTypeName ?? [],
      entitlementValue: filterMap.entitlementValue ?? [],
      entitlementSoxSeverity: filterMap.entitlementSoxSeverity ?? [],
      entitlementSysSeverity: filterMap.entitlementSysSeverity ?? [],
      entitlementPrivilegeSeverity:
        filterMap.entitlementPrivilegeSeverity ?? [],
    },
    risk: {
      riskIds,
      riskName: filterMap.riskName ?? [],
      riskType: filterMap.riskType ?? [],
      riskPriority: filterMap.riskPriority ?? [],
    },
    endpoint: {
      endpointKeys,
      endpointName: filterMap.endpointName ?? [],
    },
    globalSearch: `${searchKey ?? ''}`,
    entitlementValueKeys,
  };
};

/**
 * Creates the complete payload for entitlement list API call
 * Combines filters, pagination, and sorting parameters
 */
export const getEntitlementListPayload = (
  filterMap: Record<string, FilterMapValueType>,
  searchKey?: string | null,
  sortInAction?: EntitlementSortState,
  activeInsight?: InsightItemTypes | null,
  pagination: EntitlementPagination = { max: 10, offset: 0 }
): SodEntitlementListProps => {
  const baseFilters = getFilterPayload(filterMap, searchKey ?? null);

  return {
    filters: {
      ...baseFilters,
      insightRequests: activeInsight
        ? [{ id: String(activeInsight.id), filters: activeInsight.filters }]
        : undefined,
    },
    pagination: {
      offset: pagination.offset,
      max: pagination.max,
    },
    sort: {
      field: sortInAction?.value ?? 'entitlementValue',
      order:
        ENTITLEMENT_LIST_SORT_ACTION_MAPPING[
          sortInAction?.order ?? ENTITLEMENT_LIST_SORT_ACTION_MAPPING.DESC
        ],
    },
  };
};

/**
 * Configures tracker state for list rows
 * Adds UI interaction metadata to entitlement data
 */
export const configureTracker = (
  listData: EntitlementDetailsRowType[],
  isChecked?: false
): (EntitlementDetailsRowType & {
  tracker: {
    id: string;
    isChecked?: false;
    isRowDetailsOpen: boolean;
    isTransition: boolean;
    isFirstAddded: boolean;
  };
})[] =>
  listData.map((listRow, index) => ({
    ...listRow,
    tracker: {
      id: listRow.entitlementValueKey.toString(),
      isChecked,
      isRowDetailsOpen: false,
      isTransition: false,
      isFirstAddded: index === 0,
    },
  }));

/**
 * Gets row context for side panel
 * Extracts relevant data for detailed view
 */
export const getRowContext = (
  row: EntitlementDetailsRowType
): EntitlementSidePanelRowContextState => ({
  id: row.entitlementValueKey.toString(),
  entitlementDisplayName: row.entitlementDisplayName ?? undefined,
  entitlementValue: row.entitlementValue,
  riskCount: row.riskCount,
  userCount: row.userCount,
  violationCount: row.violationCount,
  endPointLogo: row.endPointLogo ?? undefined,
  endpointName: row.endpointName,
  entitlementTypeName: row.entitlementTypeName ?? undefined,
  entitlementSoxSeverity: row.entitlementSoxSeverity ?? undefined,
  entitlementSysSeverity: row.entitlementSysSeverity ?? undefined,
  entitlementPrivilegeSeverity: row.entitlementPrivilegeSeverity ?? undefined,
  lastUpdated: row.lastUpdated,
  entitlementDescription: row.entitlementDescription ?? undefined,
  entitlementValueKey: row.entitlementValueKey,
  glossary: row.glossary ?? undefined,
  customProperty1: row.customProperty1 ?? undefined,
});

/**
 * Creates filter payload for entitlement insights API
 * Transforms filter states to API-compatible format
 */
export const getEntitlementInsightsPayload = (
  filterMap: Record<string, FilterMapValueType>,
  searchKey: string | null,
  sortInAction: EntitlementSortState | null,
  activeInsight?: InsightItemTypes | null
) => {
  const baseFilters = getFilterPayload(filterMap, searchKey);

  return {
    filters: {
      ...baseFilters,
      insightRequests: activeInsight
        ? [{ id: String(activeInsight.id), filters: activeInsight.filters }]
        : undefined,
    },
    pagination: {
      offset: 0,
      max: 50,
    },
    sort: {
      field: sortInAction?.value ?? 'lastUpdated',
      order:
        ENTITLEMENT_LIST_SORT_ACTION_MAPPING[
          sortInAction?.order ?? ENTITLEMENT_LIST_SORT_ACTION_MAPPING.DESC
        ],
    },
  };
};

/**
 * Gets unique label entries for filter options
 * Creates filter options from entitlement data
 */
export const getUniqueLabelEntries = (
  data: EntitlementDetailsRowType[],
  key: keyof EntitlementDetailsRowType
) => {
  const uniqueValues = [...new Set(data.map((item) => item[key]))];

  return uniqueValues
    .filter(Boolean) // Removes null, undefined, false, 0, '', etc.
    .filter((value) => (value !== null ? String(value).trim() : false)) // Removes whitespace-only strings
    .map((value) => ({
      label: String(value),
      value: String(value),
      isChecked: false,
    }));
};
